export function toIsoDateString(date: string): string {
  if (!date) return '';
  if (date.includes('T')) return date;
  return new Date(date).toISOString();
}

export function toDateInputString(isoString: string): string {
  if (!isoString) return '';
  return isoString.split('T')[0];
}

export function formatLongMonthDate(dateString: string, locale: string = 'en-US'): string {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  // Taiwanese tables use the short numeric date (e.g. "2026/10/27"); these dates are only shown in tables.
  if (locale === 'zh-TW') {
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'numeric', day: 'numeric', timeZone: 'UTC' });
  }
  // Japanese uses zero-padded numeric year/month/day (e.g. "2026/08/21").
  const isJapanese = locale.startsWith('ja');
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: isJapanese ? '2-digit' : 'long',
    day: isJapanese ? '2-digit' : 'numeric',
    timeZone: 'UTC',
  });
}

export function formatLongMonthDateTime(dateString: string, locale: string = 'en-US'): string {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';

  // The time is local, so the date uses local fields too (no timeZone: 'UTC');
  // otherwise the day is wrong near midnight (e.g. 00:00-08:59 in Japan, UTC+9).
  if (locale.startsWith('ja')) {
    // Japanese uses zero-padded numeric dates and the 24-hour clock (e.g. "2026/08/21 18:30").
    const datePart = date.toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
    const time = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${datePart} ${time}`;
  }

  if (locale === 'zh-TW') {
    // Taiwanese tables use the short numeric date and the 12-hour 上午/下午 clock (e.g. "2026/10/27 下午3:30").
    const datePart = date.toLocaleDateString(locale, { year: 'numeric', month: 'numeric', day: 'numeric' });
    const time = date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${datePart} ${time}`;
  }

  const timePart = date.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    // Dutch uses the 24-hour clock with no AM/PM (e.g. "14:30").
    hour12: !locale.startsWith('nl'),
  });

  if (locale.startsWith('ko')) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}. ${month}. ${day}. ${timePart}`;
  }

  const datePart = date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return `${datePart} ${timePart}`;
}

export function formatDurationSeconds(seconds: number): string {
  if (typeof seconds !== 'number' || isNaN(seconds) || !isFinite(seconds) || seconds < 0) {
    return '0 min 0 sec';
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  return `${minutes} min ${remainingSeconds} sec`;
}

export function formatHoursMinutesRounded(totalSeconds: number | null | undefined): string {
  if (typeof totalSeconds !== 'number' || isNaN(totalSeconds) || !isFinite(totalSeconds) || totalSeconds < 0) {
    return '0';
  }

  const totalMinutesRounded = Math.round(totalSeconds / 60);

  const hours = Math.floor(totalMinutesRounded / 60);
  const minutes = totalMinutesRounded % 60;

  if (hours <= 0 && minutes <= 0) {
    return '0';
  }

  if (hours <= 0) {
    return `0 hr ${minutes} min`;
  }

  return `${hours} hr ${minutes} min`;
}

export function formatHoursMinutesSeconds(totalSeconds: number | null | undefined): string {
  if (typeof totalSeconds !== 'number' || isNaN(totalSeconds) || !isFinite(totalSeconds) || totalSeconds < 0 || totalSeconds === 0) {
    return '0 min';
  }

  const totalSecondsInt = Math.floor(totalSeconds);
  const hours = Math.floor(totalSecondsInt / 3600);
  const minutes = Math.floor((totalSecondsInt % 3600) / 60);
  const seconds = totalSecondsInt % 60;

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours} hr`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} min`);
  }
  if (seconds > 0) {
    parts.push(`${seconds} sec`);
  }

  return parts.length > 0 ? parts.join(' ') : '0';
}

export function parseRelativeDateToUtc(
  value: string,
  format: 'iso' | 'datetime' | 'long' = 'iso'
): string {
  if (!value) return value;

  let daysOffset = 0;

  const match = value.match(/Today\s*([+-])\s*(\d+)/i);
  if (match) {
    const sign = match[1];
    const numDays = parseInt(match[2], 10);
    daysOffset = sign === '+' ? numDays : -numDays;
  } else if (/Today/i.test(value)) {
    daysOffset = 0;
  } else {
    return value;
  }

  const baseDate = new Date();
  baseDate.setDate(baseDate.getDate() + daysOffset);
  const targetDate = generateRandomTime(baseDate, daysOffset === 0);

  if (format === 'datetime') {
    return targetDate.toISOString().replace('T', ' ').slice(0, 23);
  } else if (format === 'long') {
    const parts = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).formatToParts(targetDate);

    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;
    const year = parts.find(p => p.type === 'year')?.value;
    const hour = parts.find(p => p.type === 'hour')?.value;
    const minute = parts.find(p => p.type === 'minute')?.value;
    const dayPeriod = parts.find(p => p.type === 'dayPeriod')?.value;

    return `${month} ${day}, ${year} ${hour}:${minute} ${dayPeriod}`;
  }

  return targetDate.toISOString();
}

function generateRandomTime(baseDate: Date, clampToNow = false): Date {
  const date = new Date(baseDate);

  const hour =
    Math.random() < 0.7 ? 12 + Math.floor(Math.random() * 11) : 8 + Math.floor(Math.random() * 4);
  const minute = Math.floor(Math.random() * 60);
  const second = Math.floor(Math.random() * 60);
  const ms = Math.floor(Math.random() * 1000);

  date.setHours(hour, minute, second, ms);

  if (clampToNow) {
    const now = new Date();
    if (date.getTime() > now.getTime()) date.setTime(now.getTime());
  }

  return date;
}